import type { Hint, Lesson, TypingSummary } from '@typing-racer/shared';
import { clear, h, pct } from '../dom.js';
import type { LessonRecord } from '../lesson.js';

const stars = (n: number) => '★'.repeat(n) + '☆'.repeat(3 - n);

export function renderLessons(
  root: HTMLElement,
  lessons: Lesson[],
  progress: Record<string, LessonRecord>,
  recommended: Lesson,
  actions: { start(l: Lesson): void; back(): void },
): void {
  clear(root);
  const done = lessons.filter((l) => progress[l.id]).length;
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel wide stack' },
        h('div', { class: 'row', style: 'justify-content:space-between' }, h('h1', {}, '📚 Typing lessons'), h('button', { class: 'btn small ghost', onclick: actions.back }, '← Back')),
        h('p', { class: 'muted' }, `Learn touch typing step by step. ${done}/${lessons.length} lessons completed. Earn 2 stars for 95% accuracy and 3 stars for hitting the speed target too.`),
        h(
          'div',
          { class: 'lessons' },
          lessons.map((l, i) => {
            const rec = progress[l.id];
            return h(
              'div',
              { class: `lesson-card${l.id === recommended.id ? ' recommended' : ''}` },
              h('div', { class: 'lesson-num' }, String(i + 1)),
              h(
                'div',
                { class: 'lesson-body' },
                h('b', {}, l.title),
                h('div', { class: 'muted small' }, l.description),
                h('div', { class: 'row small' }, l.keys ? h('span', { class: 'keys' }, [...l.keys].slice(0, 12).map((k) => h('kbd', {}, k === ' ' ? '␣' : k))) : null, h('span', { class: 'muted' }, `Target ${l.targetWpm} WPM`)),
              ),
              h(
                'div',
                { class: 'lesson-side' },
                h('div', { class: `stars${rec ? '' : ' none'}` }, stars(rec?.stars ?? 0)),
                rec ? h('div', { class: 'muted small' }, `Best ${Math.round(rec.bestWpm)} WPM, ${pct(rec.bestAccuracy)}`) : h('div', { class: 'muted small' }, 'Not started'),
                h('button', { class: `btn small ${l.id === recommended.id ? 'primary' : 'blue'}`, onclick: () => actions.start(l) }, rec ? 'Retry' : 'Start'),
              ),
            );
          }),
        ),
      ),
    ),
  );
}

export function renderLessonResult(
  root: HTMLElement,
  lesson: Lesson,
  summary: TypingSummary,
  earned: 1 | 2 | 3,
  hints: Hint[],
  next: Lesson | null,
  actions: { retry(): void; next(): void; list(): void },
): void {
  clear(root);
  const msgs = ['', 'Finished! Aim for 95% accuracy next time.', 'Nice and accurate! Now push the speed.', 'Perfect run. On to the next lesson!'];
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel results stack', style: 'text-align:center' },
        h('div', { class: 'stars big' }, stars(earned)),
        h('h1', {}, lesson.title),
        h('p', {}, msgs[earned]),
        h(
          'div',
          { class: 'row', style: 'justify-content:center' },
          h('div', { class: 'stat' }, h('div', { class: 'v' }, String(Math.round(summary.wpm))), h('div', { class: 'l' }, 'WPM')),
          h('div', { class: 'stat' }, h('div', { class: 'v' }, pct(summary.accuracy)), h('div', { class: 'l' }, 'Accuracy')),
          h('div', { class: 'stat' }, h('div', { class: 'v' }, String(summary.errors)), h('div', { class: 'l' }, 'Typos')),
          h('div', { class: 'stat' }, h('div', { class: 'v' }, `${lesson.targetWpm}`), h('div', { class: 'l' }, 'Target WPM')),
        ),
        lesson.meanings
          ? h(
              'div',
              { class: 'review' },
              h('h2', {}, 'Review'),
              h('table', {}, h('tbody', {}, lesson.lines.map((line, i) => h('tr', {}, h('td', {}, line), h('td', { class: 'muted' }, lesson.meanings?.[i] ?? ''))))),
            )
          : null,
        hints.length ? h('div', { style: 'text-align:left' }, hints.map((hint) => h('div', { class: 'hint' }, h('b', {}, hint.title), hint.body))) : null,
        h(
          'div',
          { class: 'row', style: 'justify-content:center' },
          h('button', { class: 'btn ghost', onclick: actions.retry }, '🔁 Retry'),
          next ? h('button', { class: 'btn primary', onclick: actions.next }, `Next: ${next.title} →`) : null,
          h('button', { class: 'btn small ghost', onclick: actions.list }, lesson.meanings ? 'All sets' : 'All lessons'),
        ),
      ),
    ),
  );
}
